"use client";

import React from "react";
import { Button, Tooltip, theme } from "antd";
import type { ButtonProps as AntButtonProps, TooltipProps } from "antd";

type Variant =
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger"
    | "neutral"
    | "ghost"
    | "solid"
    | "link";

type Size = "sm" | "md" | "lg";

export type CustomButtonProps = Omit<
    AntButtonProps,
    "type" | "size" | "danger" | "ghost" | "icon"
> & {
    customVariant?: Variant;
    size?: Size;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    tooltipProps?: TooltipProps;
};

const sizeMap: Record<Size, AntButtonProps["size"]> = {
    sm: "small",
    md: "middle",
    lg: "large",
};

export function CustomButton({
    customVariant,
    size = "md",
    leftIcon,
    rightIcon,
    tooltipProps,
    children,
    className,
    style,
    ...rest
}: CustomButtonProps) {
    const { token } = theme.useToken();

    let type: AntButtonProps["type"] = "default";
    let danger = false;
    let ghost = false;

    const s: React.CSSProperties = { ...style };

    switch (customVariant) {
        case "primary":
            type = "primary";
            break;
        case "secondary":
            type = "default";
            s.borderColor ??= token.colorPrimary;
            s.color ??= token.colorPrimary;
            break;
        case "success":
            // Без локального провайдера делаем плоскую заливку (hover/active будут стандартные)
            type = "default";
            s.background ??= token.colorSuccess;
            s.color ??= token.colorTextLightSolid;
            s.borderColor ??= token.colorSuccess;
            break;
        case "warning":
            type = "default";
            s.background ??= token.colorWarning;
            s.color ??= token.colorTextLightSolid;
            s.borderColor ??= token.colorWarning;
            break;
        case "danger":
            type = "primary";
            danger = true; // корректные error-состояния из темы
            break;
        case "neutral":
            type = "default";
            s.background ??= token.colorFillTertiary;
            s.borderColor ??= token.colorBorderSecondary;
            s.color ??= token.colorText;
            break;
        case "ghost":
            type = "primary";
            ghost = true;
            break;
        case "link":
            type = "link";
            break;
    }

    const btn = (
        <Button
            {...rest}
            type={type}
            danger={danger}
            ghost={ghost}
            size={sizeMap[size]}
            className={className}
            style={s}
        >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                {leftIcon ? <span>{leftIcon}</span> : null}
                <span>{children}</span>
                {rightIcon ? <span>{rightIcon}</span> : null}
            </span>
        </Button>
    );

    if (tooltipProps) {
        return <Tooltip {...tooltipProps}>{btn}</Tooltip>;
    }

    return btn;
}

export { CustomButton as Button };
